import React, { useState } from "react";
import {
  X,
  Copy,
  RefreshCw,
  MessageSquare,
  CheckCircle2,
  ArrowRight,
} from "lucide-react";
import { palette, styles } from "../theme";

// --- THE NARRATIVE ENGINE ---
// In a real app, this would dynamically pull from the week's data.
// For this demo, we use the Week 11/12 context to generate specific scripts.

const SCRIPTS = {
  strategic: {
    label: "The Architect (Strategic)",
    description:
      "Focuses on long-term value, system design, and scope control.",
    learned:
      "Clarified the architectural boundary between Product Ed and Sales Skills. We validated that 'Content Sprawl' is our biggest risk, so I am enforcing a role-based taxonomy immediately.",
    accomplished:
      "Locked the V1 Catalog Architecture and established the 'Product Module' standard template. This ensures all future builds (including Trevi) scale without manual rework.",
    next: "Testing the 'Trevi Launch' integration to prove it works as a module within the existing system, rather than a standalone program.",
  },
  tactical: {
    label: "The Executor (Tactical)",
    description: "Focuses on speed, outputs, and checklist completion.",
    learned:
      "Confirmed AE and Specialist are the only two priority roles for the Jan launch. We are deprioritizing SE/CS content for now to hit the deadline.",
    accomplished:
      "Drafted the AE Onboarding path (First 30 Days), finalized the metadata tags for the LMS, and created the core video/quiz template.",
    next: "Finalizing the 15-20 Strategic Product list and beginning the build of the Trevi Launch module assets.",
  },
  diplomat: {
    label: "The Diplomat (Aligned)",
    description:
      "Focuses on agreement with leadership and cross-functional harmony.",
    learned:
      "Aligned with David on the 'Crawl/Walk/Run' approach. We agreed that getting the structure right now (Crawl) allows us to hand off applied training to Sales Skills in March (Walk).",
    accomplished:
      "Collaborated with RevOps to identify the revenue-driving products for the catalog. Documented the handover points between my team and Sales Enablement.",
    next: "Validating the 'Expert' certification logic to ensure it complements, rather than competes with, the Sales Pitch certification.",
  },
};

export default function WeeklyPrepModal({ onClose, activeWeek }) {
  const [selectedVibe, setSelectedVibe] = useState("strategic");
  const [copiedSection, setCopiedSection] = useState(null);

  const currentScript = SCRIPTS[selectedVibe];

  const handleCopy = (text, section) => {
    navigator.clipboard.writeText(text);
    setCopiedSection(section);
    setTimeout(() => setCopiedSection(null), 2000);
  };

  const handleCopyFull = () => {
    const fullText = `*Weekly Recap & Forward Look*\n\n*💡 Key Insight:*\n${currentScript.learned}\n\n*✅ Accomplished:*\n${currentScript.accomplished}\n\n*⏩ Next Week:*\n${currentScript.next}`;
    navigator.clipboard.writeText(fullText);
    setCopiedSection("full");
    setTimeout(() => setCopiedSection(null), 2000);
  };

  return (
    <div style={{ ...styles.modalOverlay, zIndex: 2000 }} onClick={onClose}>
      <div
        style={{
          ...styles.modalContent,
          maxWidth: "800px",
          background: "#F8FAFC",
          height: "auto",
          maxHeight: "90vh",
          overflowY: "auto",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* HEADER */}
        <div
          style={{
            padding: "24px 32px",
            background: "#FFF",
            borderBottom: `1px solid ${palette.line}`,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div>
            <div
              style={{
                fontSize: "11px",
                fontWeight: 700,
                color: palette.gold,
                textTransform: "uppercase",
                marginBottom: "4px",
              }}
            >
              Weekly Sync Prep
            </div>
            <h2
              style={{
                fontSize: "24px",
                fontWeight: 700,
                color: palette.text,
                margin: 0,
                fontFamily: "serif",
              }}
            >
              1:1 Narrative Builder
            </h2>
          </div>
          <button
            onClick={onClose}
            style={{
              border: "none",
              background: "transparent",
              cursor: "pointer",
            }}
          >
            <X size={24} color={palette.textSoft} />
          </button>
        </div>

        {/* VIBE SELECTOR */}
        <div style={{ padding: "24px 32px", background: "#FFF" }}>
          <div
            style={{
              fontSize: "12px",
              fontWeight: 700,
              color: palette.textSoft,
              marginBottom: "12px",
            }}
          >
            CHOOSE YOUR NARRATIVE STYLE
          </div>
          <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
            {Object.entries(SCRIPTS).map(([key, data]) => (
              <button
                key={key}
                onClick={() => setSelectedVibe(key)}
                style={{
                  flex: 1,
                  padding: "12px",
                  borderRadius: "8px",
                  border:
                    selectedVibe === key
                      ? `2px solid ${palette.bgSidebar}`
                      : `1px solid ${palette.line}`,
                  background: selectedVibe === key ? "#F0F9FF" : "#FFF",
                  cursor: "pointer",
                  textAlign: "left",
                  transition: "all 0.2s",
                }}
              >
                <div
                  style={{
                    fontSize: "13px",
                    fontWeight: 700,
                    color:
                      selectedVibe === key ? palette.bgSidebar : palette.text,
                  }}
                >
                  {data.label}
                </div>
                <div
                  style={{
                    fontSize: "11px",
                    color: palette.textSoft,
                    marginTop: "4px",
                  }}
                >
                  {data.description}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* CONTENT PREVIEW */}
        <div style={{ padding: "32px" }}>
          {/* SECTION 1: LEARNED */}
          <div
            style={{
              marginBottom: "24px",
              background: "#FFF",
              padding: "20px",
              borderRadius: "12px",
              border: `1px solid ${palette.line}`,
              boxShadow: "0 2px 4px rgba(0,0,0,0.02)",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "12px",
              }}
            >
              <div
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                <div
                  style={{
                    padding: "6px",
                    background: "#EFF6FF",
                    borderRadius: "6px",
                  }}
                >
                  <MessageSquare size={16} color={palette.navyLight} />
                </div>
                <span
                  style={{
                    fontSize: "12px",
                    fontWeight: 700,
                    color: palette.textSoft,
                    textTransform: "uppercase",
                  }}
                >
                  What We Learned (Alignment)
                </span>
              </div>
              <button
                onClick={() => handleCopy(currentScript.learned, "learned")}
                style={{
                  fontSize: "11px",
                  fontWeight: 600,
                  color: palette.navyLight,
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                }}
              >
                {copiedSection === "learned" ? (
                  "Copied!"
                ) : (
                  <>
                    <Copy size={12} /> Copy
                  </>
                )}
              </button>
            </div>
            <p
              style={{
                fontSize: "14px",
                lineHeight: "1.6",
                color: palette.text,
                margin: 0,
              }}
            >
              "{currentScript.learned}"
            </p>
          </div>

          {/* SECTION 2: ACCOMPLISHED */}
          <div
            style={{
              marginBottom: "24px",
              background: "#FFF",
              padding: "20px",
              borderRadius: "12px",
              border: `1px solid ${palette.line}`,
              boxShadow: "0 2px 4px rgba(0,0,0,0.02)",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "12px",
              }}
            >
              <div
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                <div
                  style={{
                    padding: "6px",
                    background: "#F0FDF4",
                    borderRadius: "6px",
                  }}
                >
                  <CheckCircle2 size={16} color={palette.success} />
                </div>
                <span
                  style={{
                    fontSize: "12px",
                    fontWeight: 700,
                    color: palette.textSoft,
                    textTransform: "uppercase",
                  }}
                >
                  What We Accomplished
                </span>
              </div>
              <button
                onClick={() =>
                  handleCopy(currentScript.accomplished, "accomplished")
                }
                style={{
                  fontSize: "11px",
                  fontWeight: 600,
                  color: palette.navyLight,
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                }}
              >
                {copiedSection === "accomplished" ? (
                  "Copied!"
                ) : (
                  <>
                    <Copy size={12} /> Copy
                  </>
                )}
              </button>
            </div>
            <p
              style={{
                fontSize: "14px",
                lineHeight: "1.6",
                color: palette.text,
                margin: 0,
              }}
            >
              "{currentScript.accomplished}"
            </p>
          </div>

          {/* SECTION 3: NEXT WEEK */}
          <div
            style={{
              marginBottom: "24px",
              background: "#FFF",
              padding: "20px",
              borderRadius: "12px",
              border: `1px solid ${palette.line}`,
              boxShadow: "0 2px 4px rgba(0,0,0,0.02)",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "12px",
              }}
            >
              <div
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                <div
                  style={{
                    padding: "6px",
                    background: "#FFF7ED",
                    borderRadius: "6px",
                  }}
                >
                  <ArrowRight size={16} color="#EA580C" />
                </div>
                <span
                  style={{
                    fontSize: "12px",
                    fontWeight: 700,
                    color: palette.textSoft,
                    textTransform: "uppercase",
                  }}
                >
                  Commitment for Next Week
                </span>
              </div>
              <button
                onClick={() => handleCopy(currentScript.next, "next")}
                style={{
                  fontSize: "11px",
                  fontWeight: 600,
                  color: palette.navyLight,
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                }}
              >
                {copiedSection === "next" ? (
                  "Copied!"
                ) : (
                  <>
                    <Copy size={12} /> Copy
                  </>
                )}
              </button>
            </div>
            <p
              style={{
                fontSize: "14px",
                lineHeight: "1.6",
                color: palette.text,
                margin: 0,
              }}
            >
              "{currentScript.next}"
            </p>
          </div>
        </div>

        {/* FOOTER ACTIONS */}
        <div
          style={{
            padding: "24px 32px",
            background: "#FFF",
            borderTop: `1px solid ${palette.line}`,
            display: "flex",
            justifyContent: "flex-end",
          }}
        >
          <button
            onClick={handleCopyFull}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              background: palette.bgSidebar,
              color: "#FFFFFF",
              border: "none",
              padding: "12px 24px",
              borderRadius: "8px",
              fontWeight: 600,
              cursor: "pointer",
              fontSize: "14px",
              boxShadow: "0 4px 12px rgba(15, 30, 54, 0.15)",
            }}
          >
            {copiedSection === "full" ? (
              "Copied to Clipboard!"
            ) : (
              <>
                <Copy size={16} /> Copy Full Agenda
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
